import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

const lumiraCollectionCleanup = () => {
  if (!/\/collections\/(bedroom-sets|sectionals)(?:[/?#]|$)/.test(window.location.pathname + '/')) return;

  const brandPattern = /^(Atelier Forn|Mavenwood|Norden Co\.|Holmsby|Halden|Westerly)\s+/i;
  const seen = new Set();

  document.querySelectorAll('li.product-grid__item').forEach((card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const image = card.querySelector('img');
    const cleanTitle = (link?.textContent || '').replace(brandPattern, '').replace(/\s+/g, ' ').trim();
    const key = cleanTitle.toLowerCase();

    if (!key) return;

    if (seen.has(key)) {
      card.remove();
      return;
    }

    if (link && link.textContent.trim() !== cleanTitle) {
      link.textContent = cleanTitle;
    }

    if (image) {
      image.alt = cleanTitle;
    }

    seen.add(key);
  });
};

lumiraCollectionCleanup();
document.addEventListener('DOMContentLoaded', lumiraCollectionCleanup, { once: true });
new MutationObserver(lumiraCollectionCleanup).observe(document.documentElement, { childList: true, subtree: true });
